/**
 * Cart Inventory Validator
 * Automatically removes out-of-stock items from cart before checkout
 */

(function() {
  'use strict';

  // Store removed items
  let removedItems = [];

  // Main validation function
  async function validateCartInventory() {
    try {
      // Get cart data
      const cart = await fetch('/cart.js', { credentials: 'same-origin' }).then(r => r.json());
      
      if (!cart.items || cart.items.length === 0) {
        return true; // Empty cart is valid
      }

      // Get pincode
      const pincode = localStorage.getItem('userPincode');
      
      // Strict 6-digit numeric validation to prevent "Pincode" placeholder leaks
      if (!pincode || !/^\d{6}$/.test(pincode)) {
        alert('Please enter a valid 6-digit pincode first');
        document.getElementById('header-pincode')?.click();
        return false;
      }

      // --- CACHE CHECK ---
      let cachedWarehouseId = localStorage.getItem('warehouseId');
      const warehouseMap = {
        "73550233855": "92862710015",
        "80137781503": "92179267839"
      };
      
      if (cachedWarehouseId && warehouseMap[cachedWarehouseId]) {
        localStorage.removeItem('warehouseId');
        cachedWarehouseId = warehouseMap[cachedWarehouseId];
        localStorage.setItem('warehouseId', cachedWarehouseId);
      }
      
      let warehouseId = cachedWarehouseId;

      if (!warehouseId) {
        // Get warehouse for pincode
        // Security: explicit CORS mode — no cookies sent cross-origin (AppScan Issue 6)
        const warehouseResponse = await fetch('https://warehouse.nutrikorner.esmagico.com/', {
          method: 'POST',
          mode: 'cors',
          credentials: 'omit',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ zipcode: pincode })
        });
        
        const warehouseData = await warehouseResponse.json();
        
        if (!warehouseData || !warehouseData[0] || !warehouseData[0].valid) {
          alert('Invalid pincode. Please enter a valid pincode.');
          return false;
        }

        warehouseId = warehouseData[0].warehouse_id.toString();
        if (warehouseId === "73550233855") {
          warehouseId = "92862710015";
        } else if (warehouseId === "80137781503") {
          warehouseId = "92179267839";
        }
        // Update cache
        localStorage.setItem('warehouseId', warehouseId);
      }

      // Get active locations
      const activeLocations = document.querySelector('.active_location_value')?.value?.split(',') || [];

      if (!activeLocations.includes(warehouseId)) {
        alert('Delivery not available for your pincode');
        return false;
      }

      // Fetch live inventory data from the new endpoint
      let liveInventory = {};
      try {
        liveInventory = await fetch('/cart?view=inventory', { credentials: 'same-origin' }).then(r => r.json());
      } catch (e) {
        console.error('[Cart Validator] Error fetching live inventory:', e);
      }

      // Check each cart item
      removedItems = [];
      
      for (const item of cart.items) {
        // Find inventory data for this variant from the live object
        const variantInventory = liveInventory[item.variant_id];
        
        if (!variantInventory) {
          // If the endpoint doesn't return data for this variant, we can't reliably validate it.
          continue;
        }

        // Get qty for the exact requested ID
        let qty = undefined;
        if (variantInventory[warehouseId] !== undefined) {
          qty = parseInt(variantInventory[warehouseId]);
        }

        if (qty === undefined) {
          // If the warehouse (new or old) isn't in the inventory object at all, it's not available
          removedItems.push({
            key: item.key,
            title: item.product_title,
            variant: item.variant_title,
            image: item.image,
            reason: 'Not available at your location'
          });
          continue;
        }

        // Check if available (qty > 10 based on your logic)
        if (qty <= 10) {
          removedItems.push({
            key: item.key,
            title: item.product_title,
            variant: item.variant_title,
            image: item.image,
            reason: 'Out of stock at your location'
          });
        }
      }

      // If items need to be removed, remove them and reload page
      if (removedItems.length > 0) {
        await removeItemsFromCart();
        
        // Store removed items in sessionStorage to show popup after reload
        sessionStorage.setItem('removedCartItems', JSON.stringify(removedItems));
        
        // Reload page - this will close cart and show popup
        window.location.reload();
        
        return false; // Don't proceed to checkout
      }

      // All items valid
      return true;

    } catch (error) {
      console.error('[Cart Validator] Error:', error);
      // On error, allow checkout to proceed
      return true;
    }
  }

  // Remove items from cart
  async function removeItemsFromCart() {
    for (const item of removedItems) {
      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: item.key,
            quantity: 0
          })
        });
      } catch (error) {
        console.error('[Cart Validator] Error removing item:', error);
      }
    }
  }

  // Show popup with removed items
  function showRemovedItemsPopup() {
    const storedItems = sessionStorage.getItem('removedCartItems');
    if (!storedItems) return;
    
    const items = JSON.parse(storedItems);
    sessionStorage.removeItem('removedCartItems'); // Clear after reading
    
    if (!items || items.length === 0) return;
    
    // Create popup
    const popup = document.createElement('div');
    popup.id = 'removed-items-popup';
    popup.innerHTML = `
      <style>
        #removed-items-popup {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.7);
          z-index: 999999 !important;
          display: flex;
          align-items: center;
          justify-content: center;
          animation: fadeIn 0.3s ease;
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        .removed-items-content {
          background: white;
          border-radius: 16px;
          max-width: 500px;
          width: 90%;
          max-height: 80vh;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          animation: slideUp 0.3s ease;
          position: relative;
          z-index: 1000000 !important;
        }
        
        @keyframes slideUp {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        
        .removed-items-header {
          padding: 24px;
          text-align: center;
          border-bottom: 1px solid #E5E7EB;
        }
        
        .removed-items-header svg {
          margin: 0 auto 16px;
        }
        
        .removed-items-header h2 {
          font-size: 20px;
          font-weight: 600;
          color: #DC2626;
          margin: 0 0 8px;
        }
        
        .removed-items-header p {
          font-size: 14px;
          color: #6B7280;
          margin: 0;
        }
        
        .removed-items-list {
          padding: 16px 24px;
          overflow-y: auto;
          flex: 1;
        }
        
        .removed-item {
          display: flex;
          gap: 12px;
          padding: 12px;
          background: #FEF2F2;
          border: 1px solid #FEE2E2;
          border-radius: 8px;
          margin-bottom: 12px;
        }
        
        .removed-item:last-child {
          margin-bottom: 0;
        }
        
        .removed-item-image {
          width: 60px;
          height: 60px;
          border-radius: 6px;
          overflow: hidden;
          background: white;
          flex-shrink: 0;
        }
        
        .removed-item-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        
        .removed-item-details {
          flex: 1;
          min-width: 0;
        }
        
        .removed-item-title {
          font-size: 14px;
          font-weight: 600;
          color: #111827;
          margin: 0 0 4px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        
        .removed-item-variant {
          font-size: 12px;
          color: #6B7280;
          margin: 0 0 4px;
        }
        
        .removed-item-reason {
          font-size: 12px;
          color: #DC2626;
          font-weight: 500;
          margin: 0;
        }
        
        .removed-items-footer {
          padding: 20px 24px;
          border-top: 1px solid #E5E7EB;
          text-align: center;
        }
        
        .removed-items-btn {
          padding: 12px 24px;
          background: #DC2626;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 15px;
          font-weight: 500;
          cursor: pointer;
          transition: background 0.2s;
        }
        
        .removed-items-btn:hover {
          background: #B91C1C;
        }
      </style>
      
      <div class="removed-items-content">
        <div class="removed-items-header">
          <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
            <circle cx="24" cy="24" r="24" fill="#FEE2E2"/>
            <path d="M24 16V26M24 30V32" stroke="#DC2626" stroke-width="3" stroke-linecap="round"/>
          </svg>
          <h2>Items Removed from Cart</h2>
          <p>The following items are not available at your location and have been removed</p>
        </div>
        
        <div class="removed-items-list">
          ${items.map(item => `
            <div class="removed-item">
              <div class="removed-item-image">
                ${item.image ? `<img src="${item.image}" alt="${item.title}">` : ''}
              </div>
              <div class="removed-item-details">
                <p class="removed-item-title">${item.title}</p>
                ${item.variant ? `<p class="removed-item-variant">${item.variant}</p>` : ''}
                <p class="removed-item-reason">${item.reason}</p>
              </div>
            </div>
          `).join('')}
        </div>
        
        <div class="removed-items-footer">
          <button class="removed-items-btn" onclick="document.getElementById('removed-items-popup').remove(); document.body.style.overflow = '';">
            Continue Shopping
          </button>
        </div>
      </div>
    `;

    document.body.appendChild(popup);
    document.body.style.overflow = 'hidden';

    // Close on overlay click
    popup.addEventListener('click', (e) => {
      if (e.target === popup) {
        popup.remove();
        document.body.style.overflow = '';
      }
    });
  }

  // Check for removed items on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', showRemovedItemsPopup);
  } else {
    showRemovedItemsPopup();
  }

  // Intercept cart button clicks
  function interceptCartOpen() {
    let hasValidated = false;
    
    document.addEventListener('click', async (e) => {
      const cartButton = e.target.closest('a[href="/cart"], a[href*="/cart"], .cart-icon, #cart-icon-bubble, [class*="cart-count"]');
      
      if (cartButton && (
        cartButton.href?.includes('/cart') || 
        cartButton.id?.includes('cart') ||
        cartButton.className?.includes('cart')
      )) {
        hasValidated = false;
        
        setTimeout(async () => {
          if (!hasValidated) {
            hasValidated = true;
            await validateCartInventory();
          }
        }, 500); // Increased to 500ms to ensure inventory data is loaded
      }
    }, true);
  }

  // Initialize
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', interceptCartOpen);
  } else {
    interceptCartOpen();
  }

})();
